/**
 * The compute gate's refusal of a `POST /geometries/{id}/diagnose`, read back
 * from the mutation cache (ENG-3091).
 *
 * Every upload fires that POST and forgets it (`useFileUpload`,
 * `useUploadRevision`, the SimOps dropzone). When the member has no Diagnosis
 * seat, or the organization no Diagnosis subscription, the gate answers 403
 * BEFORE claiming the row, so the geometry stays `idle` with no
 * `diagnosis_at`, which is exactly what "diagnosis has not started yet" looks
 * like. Nothing on the geometry records the refusal; the 403 body is the only
 * place it exists. Without this, `useDiagnosisStatus` kept polling and every
 * surface kept its spinner, with no way to tell a missing seat from a slow
 * worker.
 *
 * So each diagnose POST carries one shared `mutationKey` and a `gcTime` of
 * `Infinity` (the uploader that fired it usually unmounts on the navigation
 * that follows), and readers ask the cache for the latest POST per geometry.
 * In memory only: a page reload forgets the POST, and nothing re-posts. For
 * that case `useSubscriptionRefusal` infers the same answer from
 * `GET /subscription`, where it can be certain of it.
 */
import { useMemo } from 'react';
import { useMutationState, type Mutation } from '@tanstack/react-query';
import { handleDiagnoseGeometriesGeometryIdDiagnosePostMutation } from '../api/generated/@tanstack/react-query.gen';
import {
  parseEntitlementDenial,
  type EntitlementDenial,
} from '@/lib/entitlementDenial';

/** Spelled once, read by every diagnose POST and by `useDiagnosisRefusal`. */
export const DIAGNOSE_MUTATION_KEY = ['diagnose-geometry'] as const;

/**
 * The generated diagnose mutation, keyed so its outcome stays readable after
 * the component that fired it unmounts. Use this, not the generated options,
 * for every `POST /diagnose`.
 */
export function diagnoseMutationOptions() {
  return {
    ...handleDiagnoseGeometriesGeometryIdDiagnosePostMutation(),
    mutationKey: DIAGNOSE_MUTATION_KEY,
    gcTime: Infinity,
  };
}

function diagnosedGeometryId(mutation: Mutation<unknown, unknown, unknown>) {
  const variables = mutation.state.variables as
    | { path?: { geometry_id?: unknown } }
    | undefined;
  return variables?.path?.geometry_id;
}

/**
 * The LATEST diagnose POST for `geometryId` in this page session: the
 * refusal behind it (`null` when it succeeded, is in flight, failed for
 * another reason, or none was made), and whether it was `accepted` (in flight
 * or succeeded), which overrules any inference from the subscription.
 */
export function useLatestDiagnosePost(geometryId: string | null): {
  refusal: EntitlementDenial | null;
  accepted: boolean;
} {
  // Insertion order is submission order, so the last entry is the latest POST.
  // The error only for a settled error, so a retry in flight clears the
  // refusal until it lands.
  const outcomes = useMutationState({
    filters: {
      mutationKey: DIAGNOSE_MUTATION_KEY,
      predicate: (mutation) =>
        geometryId != null && diagnosedGeometryId(mutation) === geometryId,
    },
    select: (mutation) =>
      mutation.state.status === 'error'
        ? { error: mutation.state.error }
        : ACCEPTED,
  });
  const latest = outcomes.at(-1);
  const error = latest && latest !== ACCEPTED ? latest.error : null;
  const refusal = useMemo(() => parseEntitlementDenial(error), [error]);
  return { refusal, accepted: latest === ACCEPTED };
}

const ACCEPTED = 'accepted' as const;

/** {@link useLatestDiagnosePost}'s refusal alone. */
export function useDiagnosisRefusal(
  geometryId: string | null,
): EntitlementDenial | null {
  return useLatestDiagnosePost(geometryId).refusal;
}

/**
 * The latest diagnose POST of this page session for EVERY geometry, keyed by
 * id: its refusal, `'accepted'` (in flight or succeeded), or `null` (failed
 * for another reason). For lists, which cannot call a hook per row.
 */
export function useDiagnosePostOutcomes(): ReadonlyMap<
  string,
  EntitlementDenial | typeof ACCEPTED | null
> {
  const outcomes = useMutationState({
    filters: { mutationKey: DIAGNOSE_MUTATION_KEY },
    select: (mutation) => ({
      id: diagnosedGeometryId(mutation),
      error:
        mutation.state.status === 'error' ? mutation.state.error : ACCEPTED,
    }),
  });
  return useMemo(() => {
    const latest = new Map<
      string,
      EntitlementDenial | typeof ACCEPTED | null
    >();
    for (const { id, error } of outcomes) {
      if (typeof id !== 'string') continue;
      latest.set(
        id,
        error === ACCEPTED ? ACCEPTED : parseEntitlementDenial(error),
      );
    }
    return latest;
  }, [outcomes]);
}
